import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { getUserPlan } from '../services/userService';
import { redirectToCheckout } from '../services/stripeService';
import { useLocation } from 'react-router-dom';

const ProfilePage = () => {
  const { currentUser, updateUserProfile, updateUserPassword, reauthenticateUser, deleteUserAccount, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loadingPlanId, setLoadingPlanId] = useState(null);

  useEffect(() => {
    if (currentUser) {
      loadUserProfile();
      loadCurrentPlan();
    }
    
    // Check for checkout success
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      console.log('Checkout successful, waiting for plan update...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, location.search]);

  // Real-time plan updates via Firestore listener
  useEffect(() => {
    if (!currentUser?.uid) return;

    const userRef = doc(db, 'users', currentUser.uid);
    
    const unsubscribe = onSnapshot(
      userRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const plan = data.plan || 'free';
          setCurrentPlan(plan);
          
          // Show success message if plan changed and we're on success page
          const params = new URLSearchParams(location.search);
          if (params.get('checkout') === 'success' && plan !== 'free') {
            setSuccess(`Subscription activated! You're now on the ${plan} plan.`);
            setTimeout(() => setSuccess(''), 5000);
          }
        }
      },
      (error) => {
        console.error('Error listening to plan updates:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser, location.search]);

  const loadCurrentPlan = async () => {
    if (!currentUser?.uid) {
      setCurrentPlan('free');
      return;
    }
    try {
      const plan = await getUserPlan(currentUser.uid);
      setCurrentPlan(plan || 'free');
    } catch (err) {
      console.error('Error loading plan:', err);
      setCurrentPlan('free');
    }
  };

  const loadUserProfile = async () => {
    if (!currentUser) return;

    try {
      const displayName = currentUser.displayName || '';
      const nameParts = displayName.split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
      setEmail(currentUser.email || '');

      // Try to load from Firestore user profile
      try {
        const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.surname) {
            setLastName(data.surname);
          }
          if (data.name && !nameParts[0]) {
            setFirstName(data.name);
          }
        }
      } catch (err) {
        console.log('No user profile document found, using Auth data');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile data');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();
      await updateUserProfile(displayName);

      await setDoc(doc(db, 'userProfiles', currentUser.uid), {
        name: firstName.trim(),
        surname: lastName.trim(),
        updatedAt: new Date(),
      }, { merge: true });

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword) {
      setError('Please enter your current password');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await reauthenticateUser(currentPassword);
      await updateUserPassword(newPassword);
      setSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating password:', err);
      let errorMessage = 'Failed to update password';
      if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect current password';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setError('');

    if (!deletePassword) {
      setError('Please enter your password to confirm account deletion');
      return;
    }

    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.')) {
      return;
    }

    setLoading(true);

    try {
      await deleteUserAccount(deletePassword);
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      let errorMessage = 'Failed to delete account';
      if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleUpgrade = async (targetPlan) => {
    if (!currentUser?.uid) {
      setError('Please sign in to upgrade');
      return;
    }

    if (currentPlan === targetPlan) {
      setError(`You are already on the ${targetPlan} plan`);
      return;
    }

    if (loadingPlanId !== null) {
      return;
    }

    setError('');
    setLoadingPlanId(targetPlan);

    try {
      await redirectToCheckout(targetPlan);
    } catch (err) {
      console.error('Error starting checkout:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoadingPlanId(null);
    }
  };

  const plans = [
    {
      name: 'Free',
      id: 'free',
      price: '$0',
      period: 'Forever free',
      features: [
        '10 video analyses per month',
        'Basic analytics',
        'Email support',
        '1 folder organization',
      ],
    },
    {
      name: 'Pro',
      id: 'pro',
      price: '$29',
      period: 'Billed monthly',
      features: [
        '100 video analyses per month',
        'Advanced analytics & insights',
        'Priority support',
        'Unlimited folders',
        'Export reports (PDF, CSV)',
        'API access',
      ],
    },
    {
      name: 'Growth',
      id: 'growth',
      price: '$99',
      period: 'Billed monthly',
      features: [
        'Unlimited video analyses',
        'Team collaboration (5 users)',
        'Custom branding',
        'Advanced AI insights',
        'Dedicated account manager',
        'White-label reports',
        'Custom integrations',
      ],
    },
  ];

  return (
    <div style={{
      fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      color: '#ffffff',
      minHeight: '100vh',
      padding: '30px',
    }}>
      <h1 style={{
        fontSize: '32px',
        fontWeight: 700,
        marginBottom: '40px',
        color: '#ffffff',
      }}>
        Account Settings
      </h1>

      <div style={{
        display: 'grid',
        gap: '24px',
        maxWidth: '1200px',
      }}>
        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2', // error.light
            border: '1px solid #ef4444', // error.base
            borderRadius: '8px', // borderRadius.md
            color: '#991b1b', // error.dark
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffcdd2',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#d1fae5', // success.light
            border: '1px solid #10b981', // success.base
            borderRadius: '8px', // borderRadius.md
            color: '#065f46', // success.dark
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              style={{
                background: 'none',
                border: 'none',
                color: '#86efac',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Profile Information */}
        <section style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '24px',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#ffffff',
          }}>
            Profile Information
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '20px',
          }}>
            Update your account's profile information.
          </p>
          
          <form onSubmit={handleUpdateProfile}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '16px',
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  color: '#e5e7eb',
                }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  color: '#e5e7eb',
                }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
                color: '#e5e7eb',
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#7c3aed';
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#7c3aed',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#6d28d9';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#7c3aed';
                }
              }}
            >
              Save Changes
            </button>
          </form>
        </section>

        {/* Change Password */}
        <section style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '24px',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#ffffff',
          }}>
            Change Password
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '20px',
          }}>
            Ensure your account is using a long, random password to stay secure.
          </p>
          
          <form onSubmit={handleUpdatePassword}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
                color: '#e5e7eb',
              }}>
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#7c3aed';
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
              />
            </div>
            <div className="form-row" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '16px',
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  color: '#e5e7eb',
                }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  color: '#e5e7eb',
                }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  }}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#7c3aed',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#6d28d9';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#7c3aed';
                }
              }}
            >
              Update Password
            </button>
          </form>
        </section>

        {/* Subscription Plans */}
        <section style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '24px',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#ffffff',
          }}>
            Subscription Plans
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '20px',
          }}>
            Choose the plan that's right for you and unlock more features.
          </p>
          
          <div className="pricing-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
            marginTop: '20px',
          }}>
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan === plan.id;
              const canUpgrade = plan.id !== 'free' && currentPlan !== plan.id;
              const isLoading = loadingPlanId === plan.id;
              const isFeatured = plan.id === 'pro';

              return (
                <div
                  key={plan.id}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: isFeatured ? '2px solid #7c3aed' : '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    position: 'relative',
                    background: isFeatured ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)' : 'rgba(255, 255, 255, 0.05)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrentPlan) {
                      e.currentTarget.style.borderColor = 'rgba(124, 58, 237, 0.5)';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isFeatured) {
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {isFeatured && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#7c3aed',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '4px 12px',
                      borderRadius: '12px',
                    }}>
                      POPULAR
                    </div>
                  )}
                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    marginBottom: '12px',
                    color: '#ffffff',
                  }}>
                    {plan.name}
                  </h3>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: 700,
                    marginBottom: '4px',
                    color: '#ffffff',
                  }}>
                    {plan.price}<span style={{ fontSize: '18px', color: '#9ca3af' }}>/mo</span>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#9ca3af',
                    marginBottom: '24px',
                  }}>
                    {plan.period}
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    marginBottom: '24px',
                    textAlign: 'left',
                    padding: 0,
                  }}>
                    {plan.features.map((feature, index) => (
                      <li
                        key={index}
                        style={{
                          padding: '8px 0',
                          fontSize: '14px',
                          color: '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span style={{ color: '#7c3aed', fontWeight: 700 }}>✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => canUpgrade && handleUpgrade(plan.id)}
                    disabled={isCurrentPlan || isLoading || !canUpgrade}
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: isCurrentPlan || isLoading ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: isCurrentPlan ? 'rgba(255, 255, 255, 0.1)' : '#7c3aed',
                      color: isCurrentPlan ? '#9ca3af' : '#ffffff',
                      opacity: isLoading ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrentPlan && !isLoading && canUpgrade) {
                        e.target.style.backgroundColor = '#6d28d9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrentPlan && !isLoading && canUpgrade) {
                        e.target.style.backgroundColor = '#7c3aed';
                      }
                    }}
                  >
                    {isCurrentPlan ? 'Current Plan' : isLoading ? 'Processing...' : `Upgrade to ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Delete Account */}
        <section style={{
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '24px',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#ffffff',
          }}>
            Delete Account
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '20px',
          }}>
            Permanently delete your account and all of your data. This action cannot be undone.
          </p>
          <form onSubmit={handleDeleteAccount}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
                color: '#e5e7eb',
              }}>
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ef4444';
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                }
              }}
            >
              Delete Account
            </button>
          </form>
        </section>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .settings-grid {
            grid-template-columns: 1fr !important;
          }
          
          .pricing-grid {
            grid-template-columns: 1fr !important;
          }
          
          .form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfilePage;